import { useState } from 'react';

export default function FinalCTA() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/public/form/submit/33059925aedb8e7ecebe0f38d6820964/contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ company: '', name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <h3 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
            まずは御社が今すぐ使える<br />
            <span className="text-yellow-400">補助金を診断</span>します
          </h3>
          <p className="text-xl text-blue-100 leading-relaxed">
            無料相談で、あなたの会社に最適な補助金をご提案。<br />
            3営業日以内に診断レポートをお届けします。
          </p>
        </div>

        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
          <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="company" className="block text-sm font-bold text-gray-900 mb-2">
                  会社名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-900 focus:outline-none transition-colors text-sm"
                  placeholder="株式会社〇〇"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-900 focus:outline-none transition-colors text-sm"
                  placeholder="山田 太郎"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-900 focus:outline-none transition-colors text-sm"
                  placeholder="example@company.co.jp"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-bold text-gray-900 mb-2">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-900 focus:outline-none transition-colors text-sm"
                  placeholder="03-1234-5678"
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-2">
                お問い合わせ内容
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-900 focus:outline-none transition-colors resize-none text-sm"
                placeholder="ご相談内容やご質問をお書きください（500文字以内）"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 text-center">
                <i className="ri-check-line text-green-500 text-2xl mb-2"></i>
                <p className="text-green-700 font-bold">送信が完了しました！</p>
                <p className="text-sm text-green-600 mt-1">3営業日以内にご連絡いたします。</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 text-center">
                <i className="ri-error-warning-line text-red-500 text-2xl mb-2"></i>
                <p className="text-red-700 font-bold">送信に失敗しました</p>
                <p className="text-sm text-red-600 mt-1">お手数ですが、もう一度お試しください。</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-blue-900 to-blue-700 text-white py-4 rounded-lg font-bold text-lg hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  送信中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill mr-2"></i>
                  無料診断を依頼する
                </>
              )}
            </button>

            <p className="text-center text-sm text-gray-600">
              <i className="ri-lock-line mr-1"></i>
              お客様の情報は厳重に管理し、第三者に開示することはありません
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
